/**
 * MotionAnimator: per-frame animation for geometry wrapped with motion() in SCAD.
 *
 * Provides four independent, additive motion layers driven by the Three.js render
 * loop (useFrame) — no React re-renders, no WASM recompilation:
 *
 *   1. Axis translation  — LFO-driven displacement along a world-space direction
 *   2. Orbit             — continuous revolution around a world-space center point
 *   3. Spin              — continuous rotation around the object's local axis
 *   4. Rock              — LFO-driven oscillating rotation around a local axis
 *
 * The orbit math is intentionally simpler than the light orbit system: the orbit
 * is defined by a center point + normal vector (not an anchor + Euler tilt), so
 * the object revolves around an explicit pivot rather than having its position
 * serve as the anchor.
 *
 * All layers respect the scene-wide `paused` flag used by light LFOs and orbits.
 */

import { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { MotionDef } from '@/worker/types';
import { evaluateLFO } from '@/components/viewer/scene/SceneLighting';

// ---------------------------------------------------------------------------
// Orbit math helpers
// ---------------------------------------------------------------------------

/**
 * Build the two perpendicular basis vectors that span the orbit plane whose
 * normal is `axisVec` (already normalised).
 *
 * We construct an orthonormal frame using a stable Gram–Schmidt approach:
 * pick a reference vector that is least-parallel to `axisVec`, cross to get
 * `right`, then cross again to get `up`.
 */
function buildOrbitBasis(
  axisVec: THREE.Vector3,
): { right: THREE.Vector3; up: THREE.Vector3 } {
  // Pick a reference vector that is not parallel to axisVec
  const ref =
    Math.abs(axisVec.x) < 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);

  const right = new THREE.Vector3().crossVectors(axisVec, ref).normalize();
  const up = new THREE.Vector3().crossVectors(right, axisVec).normalize();
  return { right, up };
}

/**
 * Compute the world position on an orbit ellipse.
 *
 * center    — world-space pivot point
 * axisVec   — normalised orbit plane normal
 * radius    — semi-major radius
 * radiusB   — semi-minor radius (0 = circle)
 * angleDeg  — current angle in degrees
 * out       — output Vector3 (mutated in place)
 */
function computeOrbitFromCenter(
  center: [number, number, number],
  axisVec: THREE.Vector3,
  radius: number,
  radiusB: number,
  angleDeg: number,
  out: THREE.Vector3,
): void {
  const { right, up } = buildOrbitBasis(axisVec);
  const rA = radius;
  const rB = radiusB > 0 ? radiusB : radius;
  const rad = THREE.MathUtils.degToRad(angleDeg);
  out
    .set(center[0], center[1], center[2])
    .addScaledVector(right, rA * Math.cos(rad))
    .addScaledVector(up, rB * Math.sin(rad));
}

// ---------------------------------------------------------------------------
// Helper: normalise a vec3 tuple
// ---------------------------------------------------------------------------

function normalizeVec3(
  v: [number, number, number],
): [number, number, number] {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len < 1e-8) return [0, 1, 0]; // fallback to Y axis
  return [v[0] / len, v[1] / len, v[2] / len];
}

// ---------------------------------------------------------------------------
// AnimatedMotionMesh component
// ---------------------------------------------------------------------------

export interface AnimatedMotionMeshProps {
  /** The geometry to render. */
  geometry: THREE.BufferGeometry;
  /** The material to render with. */
  material: THREE.Material;
  /** The motion definition parsed from the SCAD motion() call. */
  motion: MotionDef;
  /** When true, all animation is frozen (shared pause toggle). */
  paused: boolean;
  /** Standard mesh props forwarded to the <mesh> element. */
  castShadow?: boolean;
  receiveShadow?: boolean;
  renderOrder?: number;
  onClick?: (e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => void;
  onPointerOver?: () => void;
  onPointerOut?: () => void;
}

/**
 * Renders a single geometry with real-time motion applied each frame.
 *
 * The component uses a <group> wrapper whose transform is updated imperatively
 * inside useFrame — zero React re-renders, zero garbage collection pressure.
 *
 * Layer application order each frame:
 *   1. Start from base position (0,0,0 in group-local space; the group is
 *      placed at the world origin so Three.js transforms do the right thing)
 *   2. Axis translation:  group.position +=  axis * LFO(t)
 *   3. Orbit:             group.position  =  orbit_center + ellipse(angle)
 *      (orbit overrides position entirely; translation is additive on top)
 *   4. Spin:              group.quaternion = fromAxisAngle(axis, speed * t)
 *   5. Rock:              group.quaternion *= fromAxisAngle(axis, LFO(t))
 */
export function AnimatedMotionMesh({
  geometry,
  material,
  motion,
  paused,
  castShadow,
  receiveShadow,
  renderOrder,
  onClick,
  onPointerOver,
  onPointerOut,
}: AnimatedMotionMeshProps) {
  const groupRef = useRef<THREE.Group>(null);

  // Accumulated angles for continuous motions (persisted across frames)
  const orbitAngle = useRef(motion.orbitOffset ?? 0);
  const spinAngle = useRef(
    THREE.MathUtils.degToRad(motion.rotateOffset ?? 0),
  );

  // Frozen time accumulator — advanced only when !paused
  const frozenTime = useRef(0);

  // Pre-compute normalized axes once (stable reference across frames)
  const translateAxisVec = useRef(
    new THREE.Vector3(...normalizeVec3(motion.translateAxis)),
  );
  const orbitAxisVec = useRef(
    new THREE.Vector3(...normalizeVec3(motion.orbitAxis)),
  );
  const rotateAxisVec = useRef(
    new THREE.Vector3(...normalizeVec3(motion.rotateAxis)),
  );
  const rockAxisVec = useRef(
    new THREE.Vector3(...normalizeVec3(motion.rockAxis)),
  );

  // Recompute axes when motion def changes (new compile)
  useEffect(() => {
    translateAxisVec.current.set(...normalizeVec3(motion.translateAxis));
    orbitAxisVec.current.set(...normalizeVec3(motion.orbitAxis));
    rotateAxisVec.current.set(...normalizeVec3(motion.rotateAxis));
    rockAxisVec.current.set(...normalizeVec3(motion.rockAxis));
    orbitAngle.current = motion.orbitOffset ?? 0;
    spinAngle.current = THREE.MathUtils.degToRad(motion.rotateOffset ?? 0);
  }, [motion]);

  // Reusable scratch objects — allocated once, mutated each frame
  const scratchPos = useRef(new THREE.Vector3());
  const scratchQuat = useRef(new THREE.Quaternion());

  useFrame((_state, delta) => {
    if (!groupRef.current) return;
    if (paused) return;

    frozenTime.current += delta;
    const time = frozenTime.current;

    // ---- Position layers ----

    // Start at world origin (the group will be positioned)
    scratchPos.current.set(0, 0, 0);

    // Layer 1: Orbit (takes over position entirely if active)
    if (motion.orbitRadius > 0) {
      orbitAngle.current =
        (orbitAngle.current + delta * motion.orbitSpeed * 360) % 360;
      computeOrbitFromCenter(
        motion.orbitCenter,
        orbitAxisVec.current,
        motion.orbitRadius,
        motion.orbitRadiusB,
        orbitAngle.current,
        scratchPos.current,
      );
    }

    // Layer 2: Axis translation (additive on top of orbit or base position)
    if (motion.translateLFO) {
      const dist = evaluateLFO(motion.translateLFO, time);
      scratchPos.current.addScaledVector(translateAxisVec.current, dist);
    }

    groupRef.current.position.copy(scratchPos.current);

    // ---- Rotation layers ----

    // Start from identity
    scratchQuat.current.identity();

    // Layer 3: Continuous spin
    if (motion.rotateSpeed !== 0) {
      spinAngle.current += delta * motion.rotateSpeed * Math.PI * 2;
      const spinQuat = new THREE.Quaternion().setFromAxisAngle(
        rotateAxisVec.current,
        spinAngle.current,
      );
      scratchQuat.current.multiply(spinQuat);
    }

    // Layer 4: Rock (LFO-driven oscillation, additive on top of spin)
    if (motion.rockLFO) {
      const angleDeg = evaluateLFO(motion.rockLFO, time);
      const rockQuat = new THREE.Quaternion().setFromAxisAngle(
        rockAxisVec.current,
        THREE.MathUtils.degToRad(angleDeg),
      );
      scratchQuat.current.multiply(rockQuat);
    }

    groupRef.current.quaternion.copy(scratchQuat.current);
  });

  return (
    <group ref={groupRef}>
      <mesh
        geometry={geometry}
        material={material}
        castShadow={castShadow}
        receiveShadow={receiveShadow}
        renderOrder={renderOrder}
        onClick={onClick}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
      />
    </group>
  );
}
